import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import type { BlogForm } from '../blogUtils';
import { BlogHtmlContent } from './BlogHtmlContent';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMText,
} from '../../../components/Primitives';

interface BlogPreviewViewProps {
  backTarget: string;
  backLabel: string;
  onBack: () => void;
  isEditMode: boolean;
  blogForm: BlogForm;
  fromDashboard: boolean;
  isPublished: boolean;
  selectedBlogId: number | null;
  isUpsertingBlog: boolean;
  isPublishingBlog: boolean;
  isPublishingFlow?: boolean;
  handleSaveDraft: () => void;
  onPublish: () => void;
  onUnpublish: (id: number) => void;
}

export const BlogPreviewView: React.FC<BlogPreviewViewProps> = ({
  backTarget,
  backLabel,
  onBack,
  isEditMode,
  blogForm,
  fromDashboard,
  isPublished,
  selectedBlogId,
  isUpsertingBlog,
  isPublishingBlog,
  isPublishingFlow = false,
  handleSaveDraft,
  onPublish,
  onUnpublish,
}) => {
  const hasHtmlContent = /<\/?[a-z][\s\S]*>/i.test(blogForm.content);

  return (
    <DMDiv className="flex-1">
      <DMButton
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-[#5E4DE0] hover:underline"
      >
        <ChevronLeft size={16} /> {backLabel}
      </DMButton>

      <DMDiv className="mb-8">
        <DMText as="h1" className="text-3xl font-bold text-gray-900">
          Preview Blog
        </DMText>
        <DMText className="text-gray-500">
          {isEditMode
            ? 'This is how your updated blog will look'
            : 'This is how your blog will look'}
        </DMText>
      </DMDiv>

      <DMDiv className="mx-auto max-w-7xl rounded-2xl bg-white p-6 shadow-sm">
        {blogForm.image && (
          <DMDiv className="mb-8 aspect-[21/9] w-full overflow-hidden rounded-xl bg-gray-100">
            <DMImage
              src={blogForm.image}
              alt={blogForm.coverImageAlt || 'Blog Cover'}
              onError={e => {
                e.currentTarget.src =
                  'https://placehold.co/600x400/E1DCFD/5E4DE0?text=No+Image';
              }}
              className="h-full w-full object-cover"
            />
          </DMDiv>
        )}

        <DMText
          as="h1"
          className="mb-4 text-3xl font-bold text-gray-900 leading-tight"
        >
          {blogForm.title || 'Untitled Blog'}
        </DMText>

        <DMDiv className="mb-8 text-sm text-gray-500">
          {new Date().toLocaleDateString()} | DemoSchool
        </DMDiv>

        {hasHtmlContent ? (
          <BlogHtmlContent html={blogForm.content} />
        ) : (
          <DMDiv className="max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
            {blogForm.content}
          </DMDiv>
        )}
      </DMDiv>

      <DMDiv className="mx-auto mt-4 flex max-w-7xl justify-end gap-4">
        {!fromDashboard && (
          <DMButton
            onClick={handleSaveDraft}
            disabled={isUpsertingBlog}
            className="rounded-full border border-gray-300 px-10 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 flex items-center justify-center min-w-[140px] disabled:opacity-50"
          >
            {isUpsertingBlog && !isPublishingFlow ? (
              <DMDiv className="flex items-center justify-center rounded-full bg-white p-1">
                <SetupLoader height="h-5" />
              </DMDiv>
            ) : (
              'Save Draft'
            )}
          </DMButton>
        )}

        {fromDashboard ? (
          <>
            {isPublished && selectedBlogId ? (
              <DMButton
                onClick={() => onUnpublish(selectedBlogId)}
                disabled={isPublishingBlog}
                className="rounded-full border border-red-200 bg-red-50 text-red-600 px-10 py-3 font-medium transition-colors hover:bg-red-100 flex items-center justify-center min-w-[140px] disabled:opacity-50"
              >
                {isPublishingBlog ? (
                  <DMDiv className="flex items-center justify-center rounded-full bg-white p-1">
                    <SetupLoader height="h-5" />
                  </DMDiv>
                ) : (
                  'Unpublish'
                )}
              </DMButton>
            ) : (
              <DMButton
                onClick={onPublish}
                disabled={isUpsertingBlog || isPublishingBlog}
                className="rounded-full bg-[#5E4DE0] px-10 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] min-w-[150px] flex items-center justify-center disabled:opacity-50"
              >
                {isPublishingBlog || (isUpsertingBlog && isPublishingFlow) ? (
                  <DMDiv className="flex items-center justify-center rounded-full bg-white p-1">
                    <SetupLoader height="h-5" />
                  </DMDiv>
                ) : isEditMode ? (
                  'Update & Publish'
                ) : (
                  'Publish Blog'
                )}
              </DMButton>
            )}
          </>
        ) : (
          <DMButton
            onClick={onPublish}
            disabled={isUpsertingBlog || isPublishingBlog}
            className="rounded-full bg-[#5E4DE0] px-10 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] min-w-[150px] flex items-center justify-center disabled:opacity-50"
          >
            {isPublishingBlog || (isUpsertingBlog && isPublishingFlow) ? (
              <DMDiv className="flex items-center justify-center rounded-full bg-white p-1">
                <SetupLoader height="h-5" />
              </DMDiv>
            ) : isEditMode ? (
              'Update & Publish'
            ) : (
              'Publish Blog'
            )}
          </DMButton>
        )}
      </DMDiv>
    </DMDiv>
  );
};
